<template>
  <div class="create-location">
    <h1>Create a Location</h1>
    <form @submit.prevent="submitForm">
      <div class="form-group">
        <label for="name">Name</label>
        <input type="text" id="name" v-model="location.name" @input="validateName" :class="{'is-invalid': validationErrors.includes('name')}" required class="form-control">
        <div v-if="validationErrors.includes('name')" class="error-feedback">Name is required.</div>
      </div>
      <div class="form-group">
        <label for="description">Description</label>
        <textarea id="description" v-model="location.description" class="form-control"></textarea>
      </div>
      <div class="upload-controls">
        <i class="bi bi-file-earmark-arrow-up-fill file-select-icon" @click="$refs.fileInput.click()"></i>
        <span>
          {{ selectedFilesCount }} file(s) selected
          <i class="bi bi-x-square-fill cancel-icon" v-if="selectedFilesCount > 0" @click="cancelSelection"></i>
        </span>
      </div>
      <input type="file" multiple @change="handleFileUpload" ref="fileInput" style="display: none;">
      <div class="form-group alert-container" v-if="validationErrors.length">
        <div class="alert alert-danger">
          <p v-for="error in validationErrors" :key="error">{{ error }}</p>
        </div>
      </div>
      <button type="submit" class="submit-button" :disabled="loading">
        <span v-if="loading" class="spinner"></span>
        <span v-else>Create Location</span>
      </button>
    </form>
    <div v-if="loading" class="overlay">
      <div class="spinner"></div>
    </div>
  </div>
</template>

<script>
import { useToast } from 'vue-toastification';
import apiService from '@/services/apiService';
import { API_ENDPOINTS } from '@/services/config';

const FORM_CACHE_KEY = "location_form";
const CACHE_EXPIRATION = 10 * 60 * 1000; // 10 minutes in milliseconds

export default {
  data() {
    return {
      location: {
        name: '',
        description: '',
      },
      validationErrors: [],
      selectedFiles: [],
      loading: false,
    };
  },
  computed: {
    selectedFilesCount() {
      return this.selectedFiles.length;
    }
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  created() {
    this.loadCachedFormData();
  },
  methods: {
    // Load cached form data if available and not expired
    loadCachedFormData() {
      const cachedData = JSON.parse(localStorage.getItem(FORM_CACHE_KEY));
      if (cachedData) {
        const { data, timestamp } = cachedData;
        const now = Date.now();

        if (now - timestamp < CACHE_EXPIRATION) {
          this.location = data;
        } else {
          localStorage.removeItem(FORM_CACHE_KEY); // Clear expired data
        }
      }
    },

    // Save form data to cache
    cacheFormData() {
      const cache = {
        data: this.location,
        timestamp: Date.now(),
      };
      localStorage.setItem(FORM_CACHE_KEY, JSON.stringify(cache));
    },

    validateName() {
      this.validateField('name');
    },
    validateField(field) {
      if (!this.location[field]) {
        this.addValidationError(field);
      } else {
        this.removeValidationError(field);
      }
    },
    addValidationError(field) {
      if (!this.validationErrors.includes(field)) {
        this.validationErrors.push(field);
      }
    },
    removeValidationError(field) {
      this.validationErrors = this.validationErrors.filter(error => error !== field);
    },
    cancelSelection() {
      this.selectedFiles = [];
      this.$refs.fileInput.value = '';
    },
    handleFileUpload(event) {
      this.selectedFiles = Array.from(event.target.files);
    },
    validateForm() {
      this.validationErrors = [];
  
      // Ensure all required fields are not empty
      const requiredFields = ['name'];
      requiredFields.forEach(field => {
        if (!this.location[field]) {
          this.addValidationError(field);
        }
      });
  
      return this.validationErrors.length === 0;
    },
    async submitLocationDetails() {
      if (!this.validateForm()) {
        this.toast.error("Form is invalid!");
        console.log("Form is invalid:", this.validationErrors);
        return;
      }
  
      try {
        const locationDetails = {
          name: this.location.name,
          description: this.location.description,
        };
        const response = await apiService.createResource(API_ENDPOINTS.locations, locationDetails);
        this.toast.success("Location created successfully!");

        if (this.selectedFiles.length > 0) {
          await apiService.uploadFiles('location', response.location_id, this.selectedFiles);
        }
  
        localStorage.removeItem(FORM_CACHE_KEY); // Clear cache after submission
        this.$router.push({ name: 'LocationDetailPage', params: { id: response.id } });
      } catch (error) {
        this.toast.error("Failed to create location!");
        console.error("Failed to submit location details:", error);
      }
    },
    async submitForm() {
      this.loading = true;
      await this.submitLocationDetails();
      this.loading = false;
    },
  },
  watch: {
    // Automatically cache form data when it changes
    location: {
      handler() {
        this.cacheFormData();
      },
      deep: true,
    },
  },
};
</script>


<style scoped lang="scss">
@import "@/assets/main.scss";

.create-location {
  max-width: 800px;
  margin: 20px auto;
  padding: 20px;
  background: $secondary-color;
  border-radius: 8px;
  box-shadow: 0 2px 15px rgba(0, 0, 0, 0.1);

  h1 {
    margin-bottom: 2rem;
    text-align: center;
  }
}
</style>
